import { baseService, GLOBAL_TAG_TYPES } from '@repo/services';
import {
  type CamelToSnakeCaseKeys,
  type InputFieldType,
  type SectionType,
  transformGraphqlResponse,
} from '@repo/utils';
import {
  getFormSections, type GetFormSectionsQuery, getFormTabs, type GetFormTabsQuery,
} from 'crm-atoms/queries';
import { getFormInputFields, type GetFormInputFieldsQuery } from 'crm-atoms/queries/forms/getFormInputFields';

interface CreateSectionInput extends CamelToSnakeCaseKeys<
Pick<SectionType, 'name'>
> { }
interface UpdateSectionInput extends Partial<Pick<SectionType, 'name' | 'sequenceNumber'>> { }
interface UpdateInputFieldInput extends Partial<Pick<InputFieldType, 'displayName' | 'sequenceNumber'>> { }
interface CreateInputFieldInput extends CamelToSnakeCaseKeys<
Pick<InputFieldType, 'displayName' | 'associateType' | 'associateId'>
> { }

const formsService = baseService.injectEndpoints({
  endpoints: (build) => ({
    getFormTabs: build.query<GetFormTabsQuery['formTabs'], { formId: string }>({
      query: ({ formId }) => ({
        url: '/graphql',
        method: 'POST',
        body: {
          query: getFormTabs,
          variables: { formId },
        },
      }),
      transformResponse: (initialRes: any) => {
        const preProcessedRes: GetFormTabsQuery = transformGraphqlResponse(initialRes);
        return preProcessedRes?.formTabs?.sort(
          (a, b) => a.sequenceNumber - b.sequenceNumber,
        ) ?? [];
      },
      providesTags(_, __, arg) {
        return [{ type: GLOBAL_TAG_TYPES.formTabs, id: arg.formId }];
      },
    }),
    getFormSections: build.query<GetFormSectionsQuery['formSections'], { formTabId: string }>({
      query: ({ formTabId }) => ({
        url: '/graphql',
        method: 'POST',
        body: {
          query: getFormSections,
          variables: { formTabId },
        },
      }),
      transformResponse: (initialRes: any) => {
        const preProcessedRes: GetFormSectionsQuery = transformGraphqlResponse(initialRes);
        return preProcessedRes?.formSections?.sort(
          (a, b) => a.sequenceNumber - b.sequenceNumber,
        ) ?? [];
      },
      providesTags(_, __, arg) {
        return [{ type: GLOBAL_TAG_TYPES.formSections, id: arg.formTabId }];
      },
    }),
    getFormInputFields: build.query<GetFormInputFieldsQuery['formInputFields'], { formSectionId: string }>({
      query: ({ formSectionId }) => ({
        url: '/graphql',
        method: 'POST',
        body: { query: getFormInputFields, variables: { formSectionId } },
      }),
      transformResponse: (initialRes: any) => {
        const preProcessedRes: GetFormInputFieldsQuery = transformGraphqlResponse(initialRes);
        return preProcessedRes?.formInputFields?.sort(
          (a, b) => a.sequenceNumber - b.sequenceNumber,
        ) ?? [];
      },
      providesTags(_, __, arg) {
        return [{ type: GLOBAL_TAG_TYPES.formInputFields, id: arg.formSectionId }];
      },
    }),
    createSection: build.mutation<any, { tabId: string; section: CreateSectionInput }>({
      query: ({ tabId, section }) => ({
        url: `/v1/forms/tabs/${tabId}/sections`,
        method: 'POST',
        body: { section },
      }),
      invalidatesTags: (_, __, arg) => [
        { type: GLOBAL_TAG_TYPES.formSections, id: arg.tabId },
      ],
    }),
    updateSection: build.mutation<any, { tabId: string; sectionId: string; section: UpdateSectionInput }>({
      query: ({ tabId, sectionId, section }) => ({
        url: `/v1/forms/tabs/${tabId}/sections/${sectionId}`,
        method: 'PUT',
        body: {
          section: {
            name: section.name,
            sequence_number: section.sequenceNumber,
          },
        },
      }),
      invalidatesTags: (_, __, arg) => [
        { type: GLOBAL_TAG_TYPES.formSections, id: arg.tabId },
      ],
    }),
    deleteSection: build.mutation<any, { tabId: string; sectionId: string }>({
      query: ({ tabId, sectionId }) => ({
        url: `/v1/forms/tabs/${tabId}/sections/${sectionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, arg) => [
        { type: GLOBAL_TAG_TYPES.formSections, id: arg.tabId },
      ],
    }),
    createInputField: build.mutation<any, {
      formSectionId: string; inputField: CreateInputFieldInput
    }>({
      query: ({ formSectionId, inputField }) => ({
        url: `/v1/forms/sections/${formSectionId}/input_fields`,
        method: 'POST',
        body: { input_field: inputField },
      }),
      invalidatesTags: (_, __, arg) => [
        { type: GLOBAL_TAG_TYPES.formInputFields, id: arg.formSectionId },
      ],
    }),
    updateInputField: build.mutation<any, {
      formSectionId: string; inputFieldId: string; inputField: UpdateInputFieldInput
    }>({
      query: ({ formSectionId, inputFieldId, inputField }) => ({
        url: `/v1/forms/sections/${formSectionId}/input_fields/${inputFieldId}`,
        method: 'PUT',
        body: {
          input_field: {
            display_name: inputField.displayName,
            sequence_number: inputField.sequenceNumber,
          },
        },
      }),
      invalidatesTags: (_, __, arg) => [
        { type: GLOBAL_TAG_TYPES.formInputFields, id: arg.formSectionId },
      ],
    }),
    deleteInputField: build.mutation<any, {
      formSectionId: string; inputFieldId: string
    }>({
      query: ({ formSectionId, inputFieldId }) => ({
        url: `/v1/forms/sections/${formSectionId}/input_fields/${inputFieldId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, arg) => [
        { type: GLOBAL_TAG_TYPES.formInputFields, id: arg.formSectionId },
      ],
    }),
  }),
});

export const {
  useGetFormTabsQuery,
  useGetFormSectionsQuery,
  useGetFormInputFieldsQuery,
  useCreateSectionMutation,
  useUpdateSectionMutation,
  useDeleteSectionMutation,
  useCreateInputFieldMutation,
  useUpdateInputFieldMutation,
  useDeleteInputFieldMutation,
} = formsService;

export default formsService;
